import React, { useEffect, useState } from 'react';
import { styled } from '@components-shared/src/stitches';
import { Skeleton } from '@components/main-components/skeleton/Skeleton';
import type { TabsProps } from '@components/main-components/tabs/Tab';
import { TabList, Tabs, TabTrigger } from '@components/main-components/tabs/Tab';
import type { TopChartsType } from '@pages-shared/topCharts';
import type { ViralChartsType } from '@pages-shared/viralCharts';

const ChartTabsOptions = {
  VIRAL: 'viral',
  TOP: 'top',
} as const;

export type ChartTabsType = (typeof ChartTabsOptions)[keyof typeof ChartTabsOptions];
const ChartTabsValues = Object.values(ChartTabsOptions);

export interface ChartProps {
  chartType: ChartTabsType;
  contentType: TopChartsType | ViralChartsType;
}

export function ChartTabs({
  onSelection,
  loading,
  value,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  icons,
  ...props
}: TabsProps & {
  icons: JSX.Element;
  onSelection?: (value: ChartTabsType) => void;
  loading?: boolean;
}) {
  const [debouncedValue, setDebouncedValue] = useState<string>(value || ChartTabsOptions.VIRAL);

  useEffect(() => {
    onSelection?.(debouncedValue as ChartTabsType);
  }, [debouncedValue, onSelection, value]);

  return (
    <TabsRow>
      <Tabs
        defaultValue={value || ChartTabsOptions.VIRAL}
        onValueChange={setDebouncedValue}
        {...props}
      >
        <TabList>
          {loading ? (
            <>
              <TabSkeleton />
              <TabSkeleton />
            </>
          ) : (
            ChartTabsValues.map(value => {
              return <CustomTabTrigger key={value} label={value} value={value} />;
            })
          )}
        </TabList>
      </Tabs>
      <TabIcons>{icons}</TabIcons>
    </TabsRow>
  );
}

const TabsRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',

  '&:first-child': {
    width: '100%',

    ' > :first-child': {
      display: 'flex',
      justifyContent: 'flex-start',
      flexGrow: 1,
    },
  },
});

const TabIcons = styled('div', {
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid $neutral100',

  // Both tabs and tabicons have a border bottom, so we need to make sure they are the same height
  height: 33,

  '@tablet': {
    height: 45,
  },
});

const TabSkeleton = styled(Skeleton, {
  borderRadius: '$extraLarge',
  marginY: 4,
  width: 50,
  height: 32,

  '@tablet': {
    width: 64,
    height: 44,
  },
});

const CustomTabTrigger = styled(TabTrigger, {
  textTransform: 'capitalize',

  title: 'L',

  '@tablet': {
    title: 'XL',
  },
});
